// NX-6284: opening balances editor for Ireland's "8% of worked hours
// (irregular)" method — PAYE only. BrightPay-style per leave year: Carry
// Over Hours + Additional Hours, max 2 entries (current and previous leave
// year).
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppRateInput from '@nxsys/components/AppRateInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import {
  IrregularHolidayOpeningBalanceEntry,
  IrregularHolidayOpeningBalances,
} from '@nxsys/types/api';
import { Col, Form, Row, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

export type IrregularHolidayOpeningBalanceValues = {
  entries: IrregularHolidayOpeningBalanceEntry[];
};

type Props = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: IrregularHolidayOpeningBalanceValues) => Promise<void>;
  openingBalances?: IrregularHolidayOpeningBalances;
  leaveYearStart?: string | null;
};

const PREVIOUS_LEAVE_YEAR_ENTRY = (
  leaveYearStart: string,
  entries: IrregularHolidayOpeningBalanceEntry[],
) => {
  const previousStart = moment(leaveYearStart)
    .subtract(1, 'year')
    .format('YYYY-MM-DD');
  return (
    entries.find((e) => e.leaveYearStart === previousStart) ?? {
      leaveYearStart: previousStart,
      carryOverHours: 0,
      additionalHours: 0,
    }
  );
};

const CURRENT_LEAVE_YEAR_ENTRY = (
  leaveYearStart: string,
  entries: IrregularHolidayOpeningBalanceEntry[],
) =>
  entries.find((e) => e.leaveYearStart === leaveYearStart) ?? {
    leaveYearStart,
    carryOverHours: 0,
    additionalHours: 0,
  };

const IrregularHolidayOpeningBalanceModal: React.FC<Props> = ({
  isOpen,
  toggle,
  onSubmit,
  openingBalances,
  leaveYearStart,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Current + previous leave year — the API accepts at most these two.
  const entries = useMemo(() => {
    if (!leaveYearStart) return [];
    const list = openingBalances?.entries ?? [];
    return [
      CURRENT_LEAVE_YEAR_ENTRY(leaveYearStart, list),
      PREVIOUS_LEAVE_YEAR_ENTRY(leaveYearStart, list),
    ];
  }, [leaveYearStart, openingBalances]);

  useEffect(() => {
    if (!isOpen) return;
    form.setFieldsValue({ entries });
  }, [isOpen, entries]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        entries: (values.entries ?? []).map(
          (e: IrregularHolidayOpeningBalanceEntry) => ({
            leaveYearStart: e.leaveYearStart,
            carryOverHours: Number(e.carryOverHours || 0),
            additionalHours: Number(e.additionalHours || 0),
          }),
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppModalContainer
      title={messages['common.editOpeningBalance'] ?? 'Edit Opening Balance'}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer form={form} onFinish={onFinish}>
        <Form.List name="entries">
          {(fields) =>
            fields.map((field, index) => (
              <div key={field.key} className="mb-4">
                <Typography.Text className="!text-[12px] !text-[#6F767E]">
                  {index === 0 ? 'Current leave year' : 'Previous leave year'}
                  {entries[index]?.leaveYearStart
                    ? ` (from ${moment(entries[index].leaveYearStart).format('DD/MM/YYYY')})`
                    : ''}
                </Typography.Text>
                <Form.Item name={[field.name, 'leaveYearStart']} hidden>
                  <input type="hidden" />
                </Form.Item>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={
                        messages['common.carryOverHours'] ??
                        'Carry Over Hours'
                      }
                      name={[field.name, 'carryOverHours']}
                    >
                      <AppRateInput min={0} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label={
                        messages['common.additionalHours'] ??
                        'Additional Hours'
                      }
                      name={[field.name, 'additionalHours']}
                    >
                      <AppRateInput min={0} />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            ))
          }
        </Form.List>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default IrregularHolidayOpeningBalanceModal;
